import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import InfoTooltip from '../InfoTooltip';
import OtpInput from './OtpInput';

/**
 * TotpEnrollmentPanel
 * -------------------
 * Shared, purely-presentational UI for the "scan a QR code with your
 * authenticator app and type the 6-digit code" part of TOTP (authenticator
 * app) MFA enrollment.
 *
 * Why this exists
 * ---------------
 * The exact same visual block (QR code + manual secret-key fallback + 6-digit
 * OTP input) was previously duplicated in three places:
 *   - SetUpMFA.js               (provider first-time enrollment)
 *   - SwitchToTotpDialog.js     (provider SMS -> authenticator switch)
 *   - PatientSetUpMFA.js         (service-user enrollment — added later)
 * Three copies means three chances to drift. Centralising the markup here keeps
 * the enrollment experience identical everywhere and gives us one place to test
 * and maintain it.
 *
 * What this component does NOT own
 * --------------------------------
 * This is deliberately "dumb": it holds no Firebase logic, no secret
 * generation, no enrollment calls, and no error/retry handling. Those differ
 * per caller (different Firestore targets, routing, analytics, re-auth) and
 * stay in the parent. The parent decides WHEN to render this panel (e.g. it
 * renders a retry prompt instead when secret generation failed).
 *
 * Two visual states, driven by `isReady`:
 *   - isReady === false -> a skeleton placeholder sized to match the real
 *     content, so the surrounding container reserves the right height while the
 *     secret/QR is being generated (prevents a layout jump).
 *   - isReady === true  -> the real QR code, manual key and OTP input.
 *
 * The submit/verify button is passed in as `children` because its label and
 * behaviour differ per caller (e.g. "Verify & Complete Setup" vs a dialog
 * footer button). When provided, the skeleton also reserves space for it.
 *
 * @param {object}   props
 * @param {boolean}  props.isReady        Whether the QR/secret is ready to show.
 * @param {string}   props.totpUri        otpauth:// URI encoded into the QR code.
 * @param {string}   props.totpSecretKey  Base32 secret shown as a manual fallback.
 * @param {string}   props.code           Current value of the 6-digit OTP input.
 * @param {Function} props.onCodeChange   Called with the new OTP value.
 * @param {boolean}  [props.otpDisabled]  Disable the OTP input (e.g. while enrolling).
 * @param {boolean}  [props.otpAutoFocus] Autofocus the OTP input when it appears.
 * @param {boolean}  props.copied         Whether the secret was just copied (Copy -> Copied!).
 * @param {Function} props.onCopySecret   Copy the manual secret key to the clipboard.
 * @param {number}   [props.qrSize=180]   Pixel size of the QR code (and skeleton box).
 * @param {string}   [props.introText]    Optional paragraph shown above the QR code.
 *                                        Omit when the caller already shows this text
 *                                        elsewhere (e.g. a dialog description).
 * @param {boolean}  [props.showAppGuidance=false] Show the "works with Google/Microsoft
 *                                        Authenticator" helper line under the QR code.
 * @param {React.ReactNode} [props.children] Submit/verify button rendered after the OTP input.
 */
export default function TotpEnrollmentPanel({
    isReady,
    totpUri,
    totpSecretKey,
    code,
    onCodeChange,
    otpDisabled = false,
    otpAutoFocus = false,
    copied,
    onCopySecret,
    qrSize = 180,
    introText,
    showAppGuidance = false,
    children,
}) {
    // Optional intro paragraph. Shared by both the skeleton and ready states so
    // the vertical rhythm does not shift between them.
    const intro = introText ? (
        <div className="text-gray-600 text-sm text-center">
            <p>{introText}</p>
        </div>
    ) : null;

    // ── Skeleton state ──────────────────────────────────────────────────────
    // Shown while the parent is generating the TOTP secret. Dimensions mirror
    // the real content so the container height stays stable.
    if (!isReady) {
        return (
            <div className="space-y-4">
                {intro}

                {/* QR code skeleton — inline width/height so the size can follow
                    the qrSize prop without relying on Tailwind arbitrary-value JIT. */}
                <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                        <div
                            className="bg-gray-100 rounded-lg animate-pulse flex items-center justify-center"
                            style={{ width: qrSize, height: qrSize }}
                        >
                            <div className="animate-spin h-6 w-6 border-2 border-primary/40 border-t-transparent rounded-full" />
                        </div>
                    </div>
                </div>

                {/* App-guidance skeleton (only when the real guidance line is shown) */}
                {showAppGuidance && (
                    <div className="flex items-start gap-2 px-1">
                        <div className="w-4 h-4 bg-gray-100 rounded-full shrink-0 animate-pulse" />
                        <div className="flex-1 h-3 bg-gray-100 rounded animate-pulse mt-0.5" />
                    </div>
                )}

                {/* Manual key skeleton */}
                <div>
                    <div className="h-3 w-40 mx-auto bg-gray-100 rounded animate-pulse mb-1.5" />
                    <div className="flex items-center gap-2">
                        <div className="flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 h-[34px] animate-pulse" />
                        <div className="shrink-0 w-14 h-[34px] bg-gray-100 rounded-md animate-pulse" />
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-gray-100" />

                {/* OTP input skeleton — six boxes matching the OtpInput layout */}
                <div className="flex justify-center gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="w-11 h-13 border-2 border-gray-200 rounded-lg bg-gray-50 animate-pulse" />
                    ))}
                </div>

                {/* Submit button skeleton — only when the caller renders a button here */}
                {children && (
                    <div className="w-full h-[40px] bg-gray-200 rounded-lg animate-pulse" />
                )}
            </div>
        );
    }

    // ── Ready state ─────────────────────────────────────────────────────────
    return (
        <div className="space-y-4">
            {intro}

            {/* QR code display */}
            <div className="flex justify-center">
                <div className="bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                    <QRCodeSVG value={totpUri} size={qrSize} level="M" includeMargin={false} />
                </div>
            </div>

            {/* Authenticator app guidance (optional) */}
            {showAppGuidance && (
                <div className="flex items-start gap-2 px-1">
                    <InfoTooltip
                        side="right"
                        content="An authenticator app generates time-based codes on your phone. Popular options include Google Authenticator and Microsoft Authenticator, both free on iOS and Android."
                    />
                    <p className="text-xs text-gray-500">
                        Works with Google Authenticator, Microsoft Authenticator, and similar apps.
                    </p>
                </div>
            )}

            {/* Manual key fallback — for authenticator apps that can't scan QR codes */}
            <div>
                <p className="text-xs text-gray-500 mb-1.5 text-center">Can't scan? Enter this key manually:</p>
                <div className="flex items-center gap-2">
                    <code className="flex-1 bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs font-mono text-gray-700 text-center tracking-wider select-all break-all">
                        {totpSecretKey}
                    </code>
                    <button
                        type="button"
                        onClick={onCopySecret}
                        className="shrink-0 px-3 py-2 text-xs font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 transition-colors"
                    >
                        {copied ? 'Copied!' : 'Copy'}
                    </button>
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Verification code input — segmented 6-box OTP UI */}
            <OtpInput
                label="Verification Code"
                value={code}
                onChange={onCodeChange}
                disabled={otpDisabled}
                autoFocus={otpAutoFocus}
            />

            {/* Caller-provided submit/verify button (optional) */}
            {children}
        </div>
    );
}
